import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import settings from "../../content/settings.json";

/**
 * CatalogRequestCTA — lead-gen block to push the catalogue request.
 *
 * Stratégie commerciale retenue : LEAD-FIRST.
 * Pas de téléchargement public du catalogue PDF tant que l'équipe commerciale
 * n'a pas qualifié le prospect. Le catalogue est envoyé par un commercial
 * Frites Dorées après contact via le formulaire `/contact?request=catalogue`
 * (qui prefill un message "demande de catalogue B2B"). Même backend
 * MailChannels que le devis.
 *
 * Une version antérieure exposait un bouton "Télécharger le PDF" gated sur
 * `settings.cta.catalog_pdf_url` — retiré pour s'aligner sur la stratégie
 * actuelle. Le champ JSON reste neutre / vide, aucun consumer ne le lit.
 *
 * Variants :
 *   • "default" — full lead band (Home, Catalog, Sector pages).
 *   • "compact" — inline strip, plus dense.
 */

const PHONE_LINK = settings?.contact?.phone_link || "+33148680303";
const PHONE_DISPLAY = settings?.contact?.phone_display || "01 48 68 03 03";

export const CatalogRequestCTA = ({
  variant = "default",
  testId = "catalog-request-cta",
  eyebrow = "Catalogue grossiste · version octobre 2026",
  headline = "Feuilletez notre catalogue grossiste en ligne.",
  tagline = "Toutes nos références avec poids, colisage et palettisation. Pour un devis, un conseiller vous répond sous 24h ouvrées.",
}) => {
  if (variant === "compact") {
    return (
      <div
        data-testid={testId}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border border-fd-gold/25 bg-[#0d0d0d] p-5 lg:p-6"
      >
        <div className="min-w-0">
          <p className="mono-label-gold mb-1.5">{eyebrow}</p>
          <p className="font-display text-lg md:text-xl uppercase tracking-tight leading-tight">{headline}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <Link
            to="/catalogue-feuilletable"
            className="btn-pill btn-primary-v2"
            data-testid={`${testId}-request`}
          >
            Voir le catalogue <ArrowUpRight size={13} />
          </Link>
          <a
            href={`tel:${PHONE_LINK}`}
            className="inline-flex items-center justify-center gap-2 px-5 py-[0.75rem] rounded-full border border-white/25 text-fd-text font-display font-medium uppercase tracking-[0.02em] text-[0.75rem] whitespace-nowrap hover:border-white transition-colors"
            data-testid={`${testId}-tel`}
          >
            <Phone size={12} /> {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid={testId}
      className="relative bg-fd-bg border-y border-fd-border py-16 lg:py-24 px-6 md:px-10 lg:px-14 overflow-hidden"
    >
      {/* Sober gold radial wash — anchors the section without becoming a
          "stock 2023 hero gradient blob". Stays under 8% opacity. */}
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(212,162,76,0.08),transparent_60%)] pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative max-w-[1400px] mx-auto flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 lg:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <p className="mono-label-gold mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-fd-gold"></span>
            <span>{eyebrow}</span>
          </p>
          <h2 className="display-xl text-4xl sm:text-5xl lg:text-6xl text-fd-text">
            {headline}
          </h2>
          <p className="mt-4 font-body text-base md:text-lg text-fd-text/70 max-w-xl">
            {tagline}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 shrink-0"
        >
          <Link
            to="/catalogue-feuilletable"
            data-cursor-hover
            data-testid={`${testId}-request`}
            className="btn-pill btn-primary-v2"
          >
            Voir le catalogue <ArrowUpRight size={14} />
          </Link>
          <Link
            to="/contact"
            data-cursor-hover
            data-testid={`${testId}-sales`}
            className="inline-flex items-center justify-center gap-2 px-7 py-[0.95rem] rounded-full border border-white/25 text-fd-text font-display font-medium uppercase tracking-[0.02em] text-[0.85rem] whitespace-nowrap hover:border-white transition-colors"
          >
            Parler à un commercial
          </Link>
          <a
            href={`tel:${PHONE_LINK}`}
            data-cursor-hover
            data-testid={`${testId}-tel`}
            className="inline-flex items-center justify-center gap-2 px-7 py-[0.95rem] rounded-full border border-white/25 text-fd-text font-display font-medium uppercase tracking-[0.02em] text-[0.85rem] whitespace-nowrap hover:border-white transition-colors lg:hidden"
          >
            <Phone size={13} /> {PHONE_DISPLAY}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
